import type {
  ApiState,
  AudioState,
  AuthState,
  GamepadState,
  GlobalInputAccess,
  HostState,
  PlayerState,
  StreamState,
  DisplayState,
  HostVersionState,
  HostQrCodeParameters,
  VideoState,
  FunctionConnectionState,
  RemoteHostList,
  LoginProfile,
  PreviewMicLevel,
  StaticImageState,
  QrCodeState,
  DriverState,
  HostSanitizationState,
  PlaycastCatalogCollection,
  TwitchSettingsState,
  DiscordSettingState,
  PlaycastEnumRunningApplications,
  PlaycastHostGamepadState,
  GameClipConfigState,
  EncoderStatus,
  PauseState,
  HostPauseState,
  PlaycastPlayerActivityState,
  StateStore,
  HostInputDominanceState,
  PlaycastHostEdition,
  PlayjectorHostEdition,
  IdentityPlatform,
  InvitationSendState,
  PlaycastWindowState,
  PlaycastPlayerInputAccess,
  HostRuleState,
} from '@playcastdotio/CoreTypes';
import config from '../../config.json';
import constants from '@playcastdotio/GlobalConfig';
import { makeStateStore } from '@playcastdotio/StateStore';
import {
  HostPresence,
  HostSettings,
  Invitation,
  LobbyPlayer,
  QueueSettingsBody,
  HostOperationalState,
  MatchmakerStateUpdatePayload,
  ProfileGetAliasesResponsePayload,
  ServicePresence,
  PlaycastMessageSystemGetMachineIdAttestationReply,
  PlaycastMessageSystemGetNonsoleReadinessReportReply,
  PlaycastMessageSystemRequestHostModeEntryReply,
  ProcessLaunchPackage,
} from '@playcastdotio/Messaging';
import { NotificationTypes } from '@playcastdotio/Settings';
import { PlaycastWebrtcPresetGroup, PlaycastWebrtcStats } from '@playcastdotio/Peer';
import { DateTime } from 'luxon';
import { ProfileState } from '@playcastdotio/SharedComponents';
import { USE_MESSAGE_ROUTER } from '../../config/featureFlags';
import { LatencyResults } from '@playcastdotio/PingClient';
import { BandwidthResult } from '@playcastdotio/PingClient';
import { machineIdAttestationState } from './machineIdAttestationState';
import { userProfileState } from '../../coordinators/rewards/stores/userProfileState';

const stringToHex = (str: string) => {
  let hex = '';
  for (let i = 0; i < str.length; i += 1) {
    hex += str.charCodeAt(i).toString(16);
  }
  return hex;
};

const parseUrl = (url: string) => {
  // ensure we have a trailing slash
  if (url[url.length - 1] !== '/') {
    url += '/';
  }
  return url;
};

const initialHostState: HostState = {
  status: 'idle',
  statusMessage: '',
  streamQrImageBase64: '',
  startTime: 0,
  showStreamQrCode: localStorage.getItem('showStreamQrCode') === 'true' || false,
  edition: window.playcast?.steamInstall ? undefined : 'standard',
};
console.log('[SteamMode Debug] Initial hostState:', initialHostState);
console.log('[SteamMode Debug] window.playcast?.steamInstall:', window.playcast?.steamInstall);

export const hostState = makeStateStore<HostState>(initialHostState, {
  // When coordinators are active, GameLaunchCoordinator owns hostState replication to nodeService
  replicate: {
    channel: 'hostState',
    targets: USE_MESSAGE_ROUTER
      ? ['remoteClient', 'hostBackend']
      : ['remoteClient', 'hostBackend', 'nodeService'],
  },
});

hostState.addListener((state) => {
  localStorage.setItem('showStreamQrCode', state.showStreamQrCode.toString());
});

export const nodeServiceLoginTokenState = makeStateStore<string>('');

export const steamInstallState = makeStateStore<{ steamInstall: boolean }>(
  { steamInstall: window.playcast?.steamInstall ?? false },
  {
    replicate: { channel: 'steamInstallState', targets: ['remoteClient'] },
  },
);
console.log('[SteamMode Debug] Initial steamInstallState:', { steamInstall: window.playcast?.steamInstall ?? false });

steamInstallState.addListener((state) => {
  console.log('[SteamMode Debug] steamInstallState changed:', state);
  hostState.updateState({
    ...hostState.currentState,
    edition: state.steamInstall ? undefined : 'standard',
  });
  console.log('[SteamMode Debug] Updated hostState:', hostState.currentState);
});

// Touch input configuration - per-machine enable/disable
// Load from localStorage to persist across sessions
const getInitialTouchInputEnabled = (): boolean => {
  try {
    const stored = localStorage.getItem('playcast.touchInputEnabled');
    return stored ? JSON.parse(stored) : false; // Default to disabled
  } catch (err) {
    console.error('[TouchInput] Failed to load persisted setting:', err);
    return false;
  }
};

export type TouchInputConfigState = {
  enabled: boolean;
};

export const touchInputConfigState = makeStateStore<TouchInputConfigState>(
  { enabled: getInitialTouchInputEnabled() },
  {
    replicate: { channel: 'touchInputConfigState', targets: ['remoteClient'] },
  },
);

console.log('[TouchInput] Initial touchInputConfigState:', { enabled: getInitialTouchInputEnabled() });

export const servicePresenceState = makeStateStore<ServicePresence>({
  running: false,
  hostConnected: false,
  playjectorServiceConnected: false,
  version: undefined,
  branch: undefined,
});


type NetworkMetricsState = {
  ping: LatencyResults;
  bandwidth: BandwidthResult;
};
const initialNetworkMetricsState: NetworkMetricsState = {
  ping: {},
  bandwidth: {
    downloadSizeBytes: 0,
    uploadSizeBytes: 0,
    downloadSpeedKbps: 0,
    uploadSpeedKbps: 0,
    downloadSpeedBits: 0,
    uploadSpeedBits: 0,
    downloadTimeMs: 0,
    uploadTimeMs: 0,
  },
};
export const networkMetricsState = makeStateStore(initialNetworkMetricsState);

type RegionOverrideState = {
  regionOverride: 'auto' | 'us-east-1' | 'us-east-2' | 'us-west-1' | 'us-west-2' | 'auto' | undefined;
};

const initialRegionOverrideState: RegionOverrideState = {
  regionOverride: (localStorage.getItem('regionOverride') as RegionOverrideState['regionOverride']) || undefined,
};
export const regionOverrideState = makeStateStore(initialRegionOverrideState);
regionOverrideState.addListener((state) => {
  localStorage.setItem('regionOverride', state.regionOverride || '');
});

const initialHostMatchmakingState: HostOperationalState = {
  globalMatchmakingToggle: true,
  matchmakeable: false, // CRITICAL: Must be false when gameState is 'idle' (no game running)
  bandwidth: {
    uploadMbps: 100,
    downloadMbps: 300,
    lastUpdated: 0,
  },
  guestAccount: !!window.playcast?.guestAccount,
  gameState: 'idle', // Initialize as idle (no game running)
};
// STRANGLER-FIG: When USE_MESSAGE_ROUTER is true, PavilionCoordinator owns this channel's replication.
// Disable replication on the legacy store to avoid two stores fighting over the same channel.
// PavilionCoordinator's hostMatchmakingStore replicates on 'hostMatchmakingState' directly.
const hostMatchmakingReplicationConfig = USE_MESSAGE_ROUTER
  ? undefined // PavilionCoordinator owns replication
  : { replicate: { channel: 'hostMatchmakingState' as const, targets: ['remoteClient' as const, 'hostBackend' as const, 'nodeService' as const] } };
export const hostMatchmakingState = makeStateStore<HostOperationalState>(initialHostMatchmakingState, hostMatchmakingReplicationConfig);

export const matchmakerState = makeStateStore<Omit<MatchmakerStateUpdatePayload, 'invitationId'>>({
  state: 'IDLE',
});

// Campaign configuration for host matchmaking
// This determines which game/campaign this host is running and will match to
type HostCampaignConfigState = {
  campaignId?: string; // Campaign identifier (e.g., 'shades', 'borderlands3', 'fortnite')
  launchPackage?: ProcessLaunchPackage;
  restartOnDisconnect?: boolean; // Whether to restart game when all players disconnect (Ad Mode only)
  // Matchmaking cooldown configuration (host-side authority).
  // See libs/Messaging/src/types/api/campaign.ts for semantics.
  cooldownStartTrigger?: 'onGameLaunched' | 'onFirstPlayerJoined' | 'never';
  cooldownPeriodMs?: number;
};
export const hostCampaignConfigState = makeStateStore<HostCampaignConfigState>({
  // undefined = unassigned, will be set by capacity manager
  // 'any' = wildcard mode (manual host selection by players)
  campaignId: undefined,
  launchPackage: undefined,
  restartOnDisconnect: undefined,
  cooldownStartTrigger: undefined,
  cooldownPeriodMs: undefined,
});

type HostMacroConfigState = {
  onLaunchMacro: string[];
  onJoinMacro: string[];
  onExitMacro: string[];
  onInactiveMacro: string[];
};
export const hostMacroConfigState = makeStateStore<HostMacroConfigState>({
  onLaunchMacro: [],
  onJoinMacro: [],
  onExitMacro: [],
  onInactiveMacro: [],
});

// Machine credentials for guest hosts (Epic/Steam login credentials)
// Sent from realtime API on guest host connection
type MachineCredentialsState = {
  epic?: {
    username: string;
    password: string;
  };
  steam?: {
    username: string;
    password: string;
  };
};
const initialMachineCredentialsState: MachineCredentialsState = {};
export const machineCredentialsState = makeStateStore<MachineCredentialsState>(initialMachineCredentialsState, {
  replicate: { channel: 'machineCredentialsState', targets: ['hostBackend'] },
});

type NotificationInAppState = {
  notification?: {
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error';
    timeoutTime: number;
  };
};
export const notificationInAppState = makeStateStore<NotificationInAppState>(
  {},
  {
    replicate: { channel: 'notificationInAppState', targets: ['remoteClient'] },
  },
);

let timeout: NodeJS.Timeout | undefined;
notificationInAppState.addListener((state) => {
  if (state.notification) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => notificationInAppState.updateState({ notification: undefined }), state.notification.timeoutTime);
  }
});

const initialGlobalInputState: GlobalInputAccess = {
  gamepad: true,
  keyboard: false,
  mouse: false,
  touch: false,
};
const globalInputState = makeStateStore<GlobalInputAccess>(initialGlobalInputState, {
  replicate: { channel: 'globalInputState', targets: ['remoteClient'] },
});

const initialGamepadState: GamepadState = {
  shareGamepadOne: false,
  shareGamepadTwo: false,
  shareGamepadThree: false,
  shareGamepadFour: false,
  autoAssign: true,
  maxAssignments: 4,
  playerMappings: {},
};
const gamepadState = makeStateStore<GamepadState>(initialGamepadState, {
  replicate: { channel: 'gamepadState', targets: ['remoteClient', 'player'] },
});

const initialPlayerState: PlayerState = {
  players: {},
};
const playerState = makeStateStore<PlayerState>(initialPlayerState, { replicate: { channel: 'playerState', targets: ['remoteClient'] } });

const initialPlayerActivityState: PlaycastPlayerActivityState = {
  inactive: false,
  players: {},
};
const playerActivityState = makeStateStore<PlaycastPlayerActivityState>(initialPlayerActivityState, {
  replicate: { channel: 'playerActivityState', targets: ['remoteClient'] },
});

type PlayerInfoState = {
  players: Record<
    string,
    {
      displayName: string;
      access: PlaycastPlayerInputAccess;
      gamepadSlots: number[];
    }
  >;
};

const initialPlayerInfoState: PlayerInfoState = {
  players: {},
};
export const playerInfoState = makeStateStore<PlayerInfoState>(initialPlayerInfoState, {
  replicate: { channel: 'playerInfoState', targets: ['remoteClient', 'player'] },
});

type UserEventsState = {
  ownedLobbies: string[] | null;
};
const initialUserEventsState: UserEventsState = {
  ownedLobbies: null,
};
export const userEventsState = makeStateStore<UserEventsState>(initialUserEventsState);

// const initialDisplaySelectionState: MediaStreamState = {
//   captureMethod: 'shadowcam',
//   availableStreamIds: [],
//   globalStreamId: '',
//   playerStreams: {},
// };
// const displaySelectionState = makeStateStore<MediaStreamState>(initialDisplaySelectionState, {
//   replicate: { channel: 'displaySelectionState', targets: ['remoteClient'] },
// });

type PlayerStatState = {
  rtt: number;
};

const initialPlayerStatsState: { players: Record<string, PlayerStatState> } = {
  players: {},
};
const playerStatState = makeStateStore<{ players: Record<string, PlayerStatState> }>(initialPlayerStatsState, {
  replicate: { channel: 'playerStatState', targets: ['remoteClient'] },
});

const initialAudioState: AudioState = {
  desktopAudioMuted: false,
  microphoneMuted: true,
  microphoneSources: [
    {
      deviceId: '0',
      label: 'None',
      selected: true,
    },
  ],
  desktopAudioSources: [
    {
      deviceId: '0',
      label: 'None',
      selected: localStorage.getItem('selectedDesktopAudioSource') === '0',
    },
    {
      deviceId: 'desktop',
      label: 'Desktop Audio',
      selected: localStorage.getItem('selectedDesktopAudioSource') === 'desktop' || true,
    },
  ],
  microphoneVolume: 50,
  desktopAudioVolume: 50,
  players: {},
};
const audioState = makeStateStore<AudioState>(initialAudioState, {
  replicate: { channel: 'audioState', targets: ['remoteClient'] },
  // splitReplicate: { channel: 'playerAudioState', targets: ['player'], splitKey: ['players'] },
});

// export const tempVideoState = makeNonSerializableStateStore<MediaStream | null>(null);

const initialMicPreviewState: PreviewMicLevel = {
  level: 0,
};
const micPreviewState = makeStateStore<PreviewMicLevel>(initialMicPreviewState, {
  replicate: { channel: 'micPreviewState', targets: ['remoteClient'], rateLimit: 1000 },
});

const initialVideoState: VideoState = {
  enabled: true,
  selectedDevice: 'display',
  cameras: [],
  shadowcams: [],
  players: {},
};
const videoState = makeStateStore<VideoState>(initialVideoState, {
  replicate: { channel: 'videoState', targets: ['remoteClient'] },
  splitReplicate: { channel: 'playerVideoState', targets: ['player'], splitKey: ['players'] },
});

const initialStaticImageState: StaticImageState = {
  staticImageBase64: '',
  players: {},
};
const staticImageState = makeStateStore<StaticImageState>(initialStaticImageState, {
  replicate: { channel: 'staticImageState', targets: ['remoteClient'] },
  splitReplicate: { channel: 'playerStaticImageState', targets: ['player'], splitKey: ['players'] },
  transformFunction: (oldState, newState) => {
    // Only set state if hostState.edition is standard
    if (hostState.currentState.edition === 'standard') {
      return newState;
    }
    return oldState;
  },
});

export const steamCredentialsState = makeStateStore<string>('');

const initialQrCodeState: QrCodeState = {
  defaultPlayerState: {
    content: '',
    enabled: false,
    scale: 1,
    position: {
      x: 0,
      y: 0,
    },
    anchor: {
      x: 0,
      y: 0,
    },
  },
  players: {},
};
const qrCodeState = makeStateStore<QrCodeState>(initialQrCodeState, {
  replicate: { channel: 'qrCodeState', targets: ['remoteClient'] },
  splitReplicate: { channel: 'playerQrCodeState', targets: ['player'], splitKey: ['players'] },
});

const initialHostQrCodeState: HostQrCodeParameters = {
  showTo: 'all',
  hideWhen: 'never',
};
const hostQrCodeState = makeStateStore<HostQrCodeParameters>(initialHostQrCodeState, {
  replicate: { channel: 'hostQrCodeState', targets: ['remoteClient', 'player'] },
});

const initialActiveShadowcamState = {
  // active: ['ShadowCam00', 'ShadowCam03', 'ShadowCam01'],
  // ShadowCam01 (856x480 @ 60fps mobile) is activated reactively by PeerCoordinator when a mobile peer connects.
  active: ['ShadowCam03'],
};
const activeShadowcamState = makeStateStore(initialActiveShadowcamState);

// const initialWindowAudioState: WindowAudioState = {
//   sources: {},
// };
// export const windowAudioState = makeStateStore<WindowAudioState>(initialWindowAudioState, {
//   replicate: { channel: 'windowAudioState', targets: ['remoteClient'] },
// });

// Non replicatable stores:
const initialAuthState: AuthState = {
  accessToken: '',
  hostAuthId: '',
};
export const authState = makeStateStore<AuthState>(initialAuthState, {
  replicate: { channel: 'authState', targets: ['nodeService'] },
});

// Owner auth ID for guest mode attribution
// Standard mode -> replicated TO node-service
// Guest mode -> receives FROM node-service (via guestAttributionState)
type OwnerAuthIdState = {
  ownerAuthId: string;
};
const isGuestAccount = window.playcast?.guestAccount ?? false;
export const OWNER_AUTH_ID_STORAGE_KEY = 'playcast_ownerAuthId';
const initialOwnerAuthIdState: OwnerAuthIdState = {
  ownerAuthId: isGuestAccount ? '' : (localStorage.getItem(OWNER_AUTH_ID_STORAGE_KEY) ?? ''),
};
export const ownerAuthIdState = makeStateStore<OwnerAuthIdState>(initialOwnerAuthIdState,
  isGuestAccount ? undefined : { replicate: { channel: 'ownerAuthIdState', targets: ['nodeService'] } },
);

// Guest attribution state - receives owner info from node-service in guest mode
type GuestAttributionState = {
  ownerAuthId: string;
};
const initialGuestAttributionState: GuestAttributionState = {
  ownerAuthId: '',
};
export const guestAttributionState = makeStateStore<GuestAttributionState>(initialGuestAttributionState);

// ownerAuthIdState is now driven by the Clerk useEffect in App.tsx (set on
// login, clear on logout). The authState listener was removed because authState
// is never explicitly cleared on logout (the app refreshes), so it could not
// produce the "clear" signal needed to gate nonsole mode.

const initialDriverState: DriverState | Record<string, boolean> = {};
export const driverState = makeStateStore<DriverState | Record<string, boolean>>(initialDriverState);

const initialSanitizationState: Partial<HostSanitizationState> = {};
export const sanitizationState = makeStateStore<Partial<HostSanitizationState>>(initialSanitizationState);

const initialApiState: ApiState = {
  remoteUrl: parseUrl(constants.API_BASE_URL),
};
export const apiState = makeStateStore<ApiState>(initialApiState);

const initialRemoteHostList: RemoteHostList = {
  selectedHostId: '',
  hosts: {},
  inactive: {},
};
export const remoteHostList = makeStateStore<RemoteHostList>(initialRemoteHostList);

const initialMachineName = '';
export const machineNameState: StateStore<string> = makeStateStore<string>(initialMachineName, {
  replicate: { channel: 'machineNameState', targets: ['remoteClient'] },
});

const initialMachineIdState = '';
export const machineIdState = makeStateStore<string>(initialMachineIdState, {
  replicate: { channel: 'machineIdState', targets: ['remoteClient'] },
});
// Todo: Move to events. Delete feature.
machineIdState.addListener((machineId) => {
  truncMachineIdState.updateState(machineId.substring(6, 20));
});

type MachineIdAttestationReply = PlaycastMessageSystemGetMachineIdAttestationReply['message'];
export const machineIdAttestationReplyState = makeStateStore<MachineIdAttestationReply | null>(null, {
  replicate: { channel: 'machineIdAttestationReplyState', targets: ['remoteClient'] },
});

// Legacy IPC transport mailbox (frozen src/api surface — ARCHITECTURE.md §Structure rule 4
// exception, documented in docs/feature-plans/activate-host-mode-button/ARCHITECTURE_REVIEW.md R3).
// Consumed ONLY by NonsoleReadinessService. Never import from components/hooks.
type NonsoleReadinessReply = PlaycastMessageSystemGetNonsoleReadinessReportReply['message'];
export const nonsoleReadinessState = makeStateStore<NonsoleReadinessReply | null>(null, {
  replicate: { channel: 'nonsoleReadinessState', targets: ['remoteClient'] },
});

// Legacy IPC transport mailbox (frozen src/api surface — ARCHITECTURE.md §Structure rule 4
// exception, documented in docs/feature-plans/activate-host-mode-button/ARCHITECTURE_REVIEW.md R3).
// Consumed ONLY by NonsoleReadinessService. Never import from components/hooks.
type HostModeEntryReply = PlaycastMessageSystemRequestHostModeEntryReply['message'];
export const hostModeEntryResultState = makeStateStore<HostModeEntryReply | null>(null, {
  replicate: { channel: 'hostModeEntryResultState', targets: ['remoteClient'] },
});

const initialTruncMachineIdState = '';
export const truncMachineIdState = makeStateStore<string>(initialTruncMachineIdState, {
  replicate: { channel: 'truncMachineIdState', targets: ['player']},
});

const initialFunctionConnectionState: FunctionConnectionState = {
  functionKey: '',
  remoteClientId: '',
  initializing: true,
  connected: false,
  error: false,
};
export const functionConnectionState = makeStateStore<FunctionConnectionState>(initialFunctionConnectionState);
// End of non replicatable stores

const initialLoginProfileState: LoginProfile = {
  email: '',
  firstName: '',
  lastName: '',
  name: '',
  picture: '',
  phoneNumber: '',
  hostAuthId: '',
  accessToken: '',
  expiresAt: 0,
};
export const loginProfileState = makeStateStore<LoginProfile>(initialLoginProfileState, {
  replicate: { channel: 'loginProfileState', targets: ['hostBackend'] },
});

const defaultPreferenceState: { email: NotificationTypes; phone: NotificationTypes } = {
  email: {
    applicationUpdates: true,
    feedbackSurveys: true,
    newsletter: true,
    playcastEvents: true,
    hostMode: true,
  },
  phone: {
    applicationUpdates: true,
    feedbackSurveys: true,
    newsletter: true,
    playcastEvents: true,
    hostMode: true,
  },
};

const savedPreferenceStateString = localStorage.getItem('preferenceState');
const savedPreferenceState = savedPreferenceStateString ? JSON.parse(savedPreferenceStateString) : null;
const initialPreferenceState: { email: NotificationTypes; phone: NotificationTypes } = savedPreferenceState || defaultPreferenceState;

// Only save to localStorage if there were no saved preferences (first time setup)
if (!savedPreferenceState) {
  localStorage.setItem('preferenceState', JSON.stringify(initialPreferenceState));
}

export const preferenceState = makeStateStore<{ email: NotificationTypes; phone: NotificationTypes }>(initialPreferenceState, {
  replicate: { channel: 'preferenceState', targets: ['remoteClient'] },
});

preferenceState.addListener((state) => {
  const jsonString = JSON.stringify(state);
  try {
    localStorage.setItem('preferenceState', jsonString);
  } catch (error) {
    console.error('[Error saving to localStorage:', error);
  }
});

const initialDisplayState: DisplayState = {
  info: {
    selectedDisplayId: -1,
  },
  displays: [],
  sourceProperties: [],
};
export const displayState = makeStateStore<DisplayState>(initialDisplayState, {
  replicate: { channel: 'displayState', targets: ['remoteClient'] },
});

export const hostWebrtcStatsState = makeStateStore<Record<string, PlaycastWebrtcStats | null>>({});

const initialVersionState: HostVersionState = {
  version: '0.0.0',
  hasUpdate: false,
  updateVersion: '',
  autoUpdate: true,
  downloadingUpdate: false,
  downloadProgress: 0,
  downloadFailed: false,
  downloadComplete: false,
};
export const versionState = makeStateStore<HostVersionState>(initialVersionState, {
  replicate: { channel: 'versionState', targets: ['remoteClient'] },
});

// const initialInvitationState: InvitationInfoResBody = {
//   id: '',
//   name: 'Unnamed session',
//   location: 'Unknown location',
//   description: '',
//   manifest: {},
//   status: '',
//   instantConnect: false,
//   queues: [],
//   broadcastActive: false,
//   pendingCount: 0,
// };
// const invitationStore = makeStateStore<InvitationInfoResBody>(initialInvitationState, {
//   replicate: { channel: 'invitationStore', targets: ['remoteClient'] },
// });

// default state uses a lobby invitation
const initialInvitationV2State: Invitation = {
  id: '',
  name: 'Unnamed session',
  description: '',
  invalid: false,
  invalidReasons: [],
  type: 'lobby',
  authRequired: false,
  maxLinkUse: -1,
  linkExpiry: DateTime.now().plus({ days: 1 }).toISO(),
  visibility: 'public',
  owner: {
    avatar: '',
    username: '',
    profileId: '',
  },
  manifest: {
    machineId: '',
    regionName: '',
    lobbyOptions: {
      canJoin: true,
      // set max lobby size to 32 by default
      maxLobbySize: 32,
      ignoreHostMaxPlayerCount: false,
      autoAccept: true,
    },
    defaultPlayerSettings: {
      gamepad: true,
      keyboard: false,
      mouse: false,
    },
  },
};
export const invitationStateV2 = makeStateStore<Invitation>(initialInvitationV2State, {
  replicate: { channel: 'invitationStateV2', targets: ['remoteClient'] },
});
const savedInvitationState = localStorage.getItem('currentInvitation');
if (savedInvitationState && hostState.currentState.edition === 'standard') {
  const parsedInvitation = JSON.parse(savedInvitationState);
  if (parsedInvitation.type === 'lobby' && parsedInvitation.owner.profileId === authState.currentState.hostAuthId) {
    // Use structuredClone to avoid mutating the initial state
    const mergedInvitation = structuredClone(initialInvitationV2State);
    // Deep merge parsedInvitation into mergedInvitation
    const deepMerge = (target: any, source: any) => {
      for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key]) && key in target) {
          deepMerge(target[key], source[key]);
        } else {
          target[key] = source[key];
        }
      }
      return target;
    };
    deepMerge(mergedInvitation, parsedInvitation);
    invitationStateV2.updateState(mergedInvitation);
  }
}
authState.addListener((state) => {
  if (state.hostAuthId !== '' && invitationStateV2.currentState.id === '') {
    const savedInvitation = localStorage.getItem('currentInvitation');
    if (savedInvitation && hostState.currentState.edition === 'standard') {
      const parsedInvitation = JSON.parse(savedInvitation);
      if (parsedInvitation.type === 'lobby' && parsedInvitation.owner.profileId === state.hostAuthId) {
        invitationStateV2.updateState(parsedInvitation);
      }
    }
  }
});

const initialHostPresenceState: HostPresence = {
  status: 'idle',
  lastUpdated: 0,
  playerCount: 0,
  hostPeerId: '',
  remoteHostPeerId: '',
  guestAccount: !!window.playcast?.guestAccount, // Ad mode indicator
  isCloudHost: false, // Set by server during auth via cloudHostToken validation
};
export const hostPresenceState: StateStore<HostPresence> = makeStateStore<HostPresence>(initialHostPresenceState, {
  replicate: { channel: 'hostPresenceState', targets: ['remoteClient'] },
});

const initialHostSettingsState: HostSettings = {
  maxPlayerCount: 4,
};
const hostSettingsState = makeStateStore<HostSettings>(initialHostSettingsState, {
  replicate: { channel: 'hostSettingsState', targets: ['remoteClient'] },
});

type LobbyState = {
  type: 'instant' | 'lobby';
  knocks: {
    pending: LobbyPlayer[];
    accepted: LobbyPlayer[];
  };
};
const initialLobbyStateV2: LobbyState = {
  type: 'lobby',
  knocks: {
    pending: [],
    accepted: [],
  },
};
const lobbyStateV2 = makeStateStore<LobbyState>(initialLobbyStateV2, {
  replicate: { channel: 'lobbyStateV2', targets: ['remoteClient'] },
});

type QueueState = {
  id: string;
  enabled: boolean;
  otherKnocks: {
    accepted: string[];
    exited: string[];
    joined: string[];
    rejected: string[];
  };
  knocks: {
    pendingPlayers: string[];
    acceptedPlayers: string[];
    rejectedPlayers: string[];
  };
  settings: QueueSettingsBody;
};

const initialQueueState: QueueState = {
  id: '',
  enabled: false,
  knocks: {
    pendingPlayers: [],
    acceptedPlayers: [],
    rejectedPlayers: [],
  },
  otherKnocks: {
    accepted: [],
    exited: [],
    joined: [],
    rejected: [],
  },
  settings: {
    acceptAll: true,
    authRequired: false,
    prioritizeNewPlayers: false,
    description: '',
    maxKnocks: -1,
    maxPlayers: 32,
  },
};
const queueState = makeStateStore<QueueState>(initialQueueState, {
  replicate: { channel: 'queueState', targets: ['remoteClient'] },
});

export type KnockMetadata = {
  metadata: {
    [key: string]: {
      pending: boolean;
      name: string;
      playCount: number;
      timestamp: number;
      acceptedTimestamp: number;
    };
  };
};

const initialKnockMetadataState: KnockMetadata = {
  metadata: {},
};
const knockMetadataState = makeStateStore(initialKnockMetadataState, {
  replicate: { channel: 'knockMetadataState', targets: ['remoteClient'] },
});

const initialPauseInputstate: PauseState = {
  paused: false,
  players: {},
};

export const pauseInputState = makeStateStore(initialPauseInputstate, {
  replicate: { channel: 'pauseInputState', targets: ['remoteClient', 'player'] },
});

const initialHostPauseInputState: HostPauseState = {
  paused: false,
};
export const hostPauseInputState = makeStateStore(initialHostPauseInputState);

const initialHostRuleState: HostRuleState = {
  showVideoPreview: true,
  videoPreviewCamera: config.PREVIEW_SHADOWCAM,
  hostAutoAccept: true,
  apiAutoAccept: false,
  hostHandlesQueueAccepting: true,
  enableGamepadFiltering: false,
};
const hostRuleState = makeStateStore(initialHostRuleState, {
  replicate: { channel: 'hostRuleState', targets: ['remoteClient', "hostBackend"] },
});

hostRuleState.addListener((state) => {
  localStorage.setItem('enableGamepadFiltering', state.enableGamepadFiltering.toString());
});

const showStaticImageState = makeStateStore<{ showStaticImage: boolean }>(
  {
    showStaticImage: false,
  },
  {
    replicate: { channel: 'showStaticImageState', targets: ['player'] },
  },
);

const initialInvitationSendState: InvitationSendState = {
  reachedEmailInviteLimit: false,
  emailLimitExpiry: 0,
  emailLimit: 0,
  emailCount: 0,
  reachedSmsInviteLimit: false,
  smsLimitExpiry: 0,
  smsLimit: 0,
  smsCount: 0,
};

const invitationSendState = makeStateStore(initialInvitationSendState, {
  replicate: { channel: 'invitationSendState', targets: ['remoteClient'] },
});

const webrtcPresetGroups = makeStateStore<PlaycastWebrtcPresetGroup>({} as any, {
  replicate: { channel: 'webrtcPresetGroups', targets: ['remoteClient'] },
});

const defaultTwitchSettings = {
  isTwitchBotEnabled: true,
  isSendEndAnnouncement: true,
  isSendPlaycastInfo: true,
  isSendStartAnnouncement: true,
  isSendStreamUrlReminder: true,
  shouldRespondToChat: true,
  twitchDisplayName: '',
};
const savedTwicthSettings = JSON.parse(localStorage.getItem('twitchSettings') || '""') || defaultTwitchSettings;
const initialTwitchSettingsState: TwitchSettingsState = savedTwicthSettings ? savedTwicthSettings : defaultTwitchSettings;
localStorage.setItem('twitchSettings', JSON.stringify(initialTwitchSettingsState));

export const twitchSettingsState = makeStateStore(initialTwitchSettingsState, {
  replicate: { channel: 'twitchSettingsState', targets: ['remoteClient'] },
});

twitchSettingsState.addListener((state) => {
  const totalState = {
    ...defaultTwitchSettings,
    ...state,
  };
  localStorage.setItem('twitchSettings', JSON.stringify(totalState));
});

const defaultDiscordSettings = {
  isDiscordBotEnabled: true,
  discordDisplayName: '',
};
const savedDiscordSettings = JSON.parse(localStorage.getItem('discordSettings') || '""') || defaultDiscordSettings;
const initialDiscordSettingsState: DiscordSettingState = savedDiscordSettings ? savedDiscordSettings : defaultDiscordSettings;
localStorage.setItem('discordSettings', JSON.stringify(initialDiscordSettingsState));

export const discordSettingsState = makeStateStore(initialDiscordSettingsState, {
  replicate: { channel: 'discordSettingsState', targets: ['remoteClient'] },
});

discordSettingsState.addListener((state) => {
  const totalState = {
    ...defaultDiscordSettings,
    ...state,
  };
  localStorage.setItem('discordSettings', JSON.stringify(totalState));
});

const initialProcessCatalog: PlaycastCatalogCollection = {
  default: [],
  epic: [],
  steam: [],
  windows: [],
};
export const processCatalogState = makeStateStore<PlaycastCatalogCollection>(initialProcessCatalog, {
  replicate: { channel: 'processCatalogState', targets: ['remoteClient'] },
});

const initialRunningApplicationState: PlaycastEnumRunningApplications = [];
export const runningApplicationState = makeStateStore<PlaycastEnumRunningApplications>(initialRunningApplicationState, {
  replicate: { channel: 'runningApplicationState', targets: ['remoteClient'] },
});

type IceServerState = {
  hostIceServers: { urls: string[]; username: string; credential: string };
  remoteHostIceServers: { urls: string[]; username: string; credential: string };
  remoteClientIceServers: { urls: string[]; username: string; credential: string };
  allowPeerConnections: boolean;
};
const initialIceServerState: IceServerState = {
  hostIceServers: { urls: [], username: '', credential: '' },
  remoteHostIceServers: { urls: [], username: '', credential: '' },
  remoteClientIceServers: { urls: [], username: '', credential: '' },
  allowPeerConnections: true,
};
export const iceServerState = makeStateStore<IceServerState>(initialIceServerState);

const initialAllowPeerConnections =
  localStorage.getItem('allowPeerConnections') === 'true' || localStorage.getItem('allowPeerConnections') === null;
iceServerState.updateState({
  ...iceServerState.currentState,
  allowPeerConnections: initialAllowPeerConnections,
});

// const defaultAudioSources = {
//   sources: [
//     {
//       name: 'None',
//     },
//     {
//       name: 'Desktop Audio',
//     },
//   ],
// };

// export const desktopAudioSources = makeStateStore(defaultAudioSources, {
//   replicate: { channel: 'desktopAudioSources', targets: ['remoteClient'] },
// });

const initialQrCodeBase64State = '';
const qrCodeBase64State = makeStateStore<string>(initialQrCodeBase64State, {
  replicate: { channel: 'qrCodeBase64State', targets: ['hostBackend'] },
});

const initialQrCodeGenerationState = {
  isGenerating: false,
  isComplete: false,
  error: false,
};
export const qrCodeGenerationState = makeStateStore(initialQrCodeGenerationState);

const initialHostGamepadState: PlaycastHostGamepadState = {
  gamepads: [],
  raw: {},
};
export const hostGamepadState = makeStateStore<PlaycastHostGamepadState>(initialHostGamepadState, {
  replicate: { channel: 'hostGamepadState', targets: ['remoteClient'] },
});

const initialGameClipConfigState: GameClipConfigState = {
  enabled: true,
  recordHotkey: 'F8',
  bufferDurationSeconds: 5,
  cameraName: 'ShadowCam03',
  directoryPath: '',
  ...JSON.parse(localStorage.getItem('gameClipConfig') || '{}'),
};
const gameClipConfigState = makeStateStore<GameClipConfigState>(initialGameClipConfigState, {
  replicate: { channel: 'gameClipConfigState', targets: ['remoteClient'] },
});

export const videosDirState = makeStateStore<string>('');
videosDirState.addListener((state) => {
  gameClipConfigState.updateState({
    ...gameClipConfigState.currentState,
    directoryPath: state,
  });
});

gameClipConfigState.addListener((state) => {
  const validConfig = {
    enabled: state.enabled,
    recordHotkey: state.recordHotkey,
    bufferDurationSeconds: state.bufferDurationSeconds,
    cameraName: state.cameraName,
    directoryPath: state.directoryPath,
  };
  localStorage.setItem('gameClipConfig', JSON.stringify(validConfig));
});

const initialGameClipStatusState: EncoderStatus = {
  result: 'idle',
  cameraName: '',
};
export const gameClipStatusState = makeStateStore<EncoderStatus>(initialGameClipStatusState, {
  replicate: { channel: 'gameClipStatusState', targets: ['remoteClient'] },
});

const initialHostInputDominanceState: HostInputDominanceState = {
  mouse: false,
  keyboard: false,
};
export const hostInputDominanceState = makeStateStore<HostInputDominanceState>(initialHostInputDominanceState, {
  replicate: { channel: 'hostInputDominanceState', targets: ['remoteClient'] },
});

const initialAutomaticUpdatesEnabledState = false;
export const automaticUpdatesEnabledState = makeStateStore<boolean>(initialAutomaticUpdatesEnabledState, {
  replicate: { channel: 'automaticUpdatesEnabledState', targets: ['remoteClient'] },
});

const initialProfileState: ProfileState = {
  profile: null,
  invitations: [],
  invitationsLoading: false,
  isLoading: false,
  error: null,
  isOwner: true,
};
export const profileState = makeStateStore<ProfileState>(initialProfileState, {
  replicate: { channel: 'profileState', targets: ['remoteClient'] },
});

const initialMatchmakeToPublicMachineState = {
  matchmakeToPublicMachine: false,
};
export const matchmakeToPublicMachineState = makeStateStore(initialMatchmakeToPublicMachineState, {
  replicate: { channel: 'matchmakeToPublicMachineState', targets: ['remoteClient'] },
});

export const ownerAliasState = makeStateStore<ProfileGetAliasesResponsePayload | null>(null, {
  replicate: { channel: 'ownerAliasState', targets: ['remoteClient'] },
});

export const matchmakerOverrideState = makeStateStore<boolean>(false, {
  replicate: { channel: 'matchmakerOverrideState', targets: ['remoteClient'] },
});

type VpnInfoState = {
  vpnHostname: string;
  steamCafeHostname: string;
};

export type AfkPreventionState = {
  enabled: boolean;
  keyCode: number;
  intervalSeconds: number;
  keyDisplayName: string;
};

const initialVpnInfoState: VpnInfoState = {
  vpnHostname: '',
  steamCafeHostname: '',
};

// AFK prevention state (persisted)
const defaultAfkPreventionState: AfkPreventionState = {
  enabled: false,
  keyCode: 32, // Space key
  intervalSeconds: 30,
  keyDisplayName: 'Space',
};
const savedAfkPreventionState = JSON.parse(localStorage.getItem('afkPreventionState') || '""') || defaultAfkPreventionState;
const initialAfkPreventionState: AfkPreventionState = {
  ...defaultAfkPreventionState,
  ...savedAfkPreventionState,
};

export const vpnInfoState = makeStateStore<VpnInfoState>(initialVpnInfoState, {
  replicate: { channel: 'vpnInfoState', targets: ['hostBackend'] },
});
export const afkPreventionState = makeStateStore<AfkPreventionState>(initialAfkPreventionState);
afkPreventionState.addListener((state) => {
  try {
    localStorage.setItem('afkPreventionState', JSON.stringify(state));
  } catch (err) {
    // ignore persistence errors
  }
});

const initialPlayjectorHostMode: PlayjectorHostEdition = {
  edition: undefined,
  matchmakable: false,
  serverLogin: false,
  guestAccount: false,
  steamCredentials: '',
  steamLaunched: false,
  steamWindow: false,
  userLoggedIn: false,
  gameLaunched: false,
  gameName: '',
  gameId: 0,
};
export const playjectorHostModeState = makeStateStore<PlayjectorHostEdition>(initialPlayjectorHostMode, {
  replicate: { channel: 'playjectorHostModeState', targets: ['remoteClient'] },
});

export type AdModeConfig = {
  enabled: boolean;
  steamId: string;
  launchArgs: string;
};

const initialAdModeConfig: AdModeConfig = {
  enabled: false,
  steamId: '',
  launchArgs: '',
};
export const adModeState = makeStateStore<AdModeConfig>(initialAdModeConfig, {
  replicate: { channel: 'adModeState', targets: ['remoteClient'] },
});

export const windowState = makeStateStore<PlaycastWindowState>('normal', {
  replicate: { channel: 'windowState', targets: ['hostBackend'] },
});

// Machine ID attestation state
export { machineIdAttestationState };
export { userProfileState };

// Player name state for lobby join (like in marketing app)
const initialPlayerNameState: { name: string } = { name: localStorage.getItem('name') || '' };
export const playerNameState = makeStateStore<{ name: string }>(initialPlayerNameState);

// Performance mode state - disables UI for ad mode machines
// Note: Only persists to localStorage when manually set, not when auto-enabled by ad mode
type PerformanceModeState = {
  enabled: boolean;
};
const initialPerformanceModeState: PerformanceModeState = {
  enabled: localStorage.getItem('performanceMode') === 'true' || false,
};
export const performanceModeState = makeStateStore<PerformanceModeState>(initialPerformanceModeState);

const sharedStateStores = {
  hostState,
  steamInstallState,
  nodeServiceLoginTokenState,
  servicePresenceState,
  hostMatchmakingState,
  matchmakerOverrideState,
  profileState,
  ownerAliasState,
  networkMetricsState,
  notificationInAppState,
  matchmakerState,
  globalInputState,
  gamepadState,
  playerState,
  playerStatState,
  playerActivityState,
  // displaySelectionState,
  audioState,
  videoState,
  staticImageState,
  micPreviewState,
  displayState,
  versionState,
  userEventsState,
  qrCodeState,
  hostQrCodeState,
  activeShadowcamState,
  authState,
  ownerAuthIdState,
  guestAttributionState,
  remoteHostList,
  // windowAudioState,
  // invitationStore,
  invitationStateV2,
  lobbyStateV2,
  hostPresenceState,
  queueState,
  knockMetadataState,
  pauseInputState,
  hostPauseInputState,
  apiState,
  driverState,
  sanitizationState,
  hostWebrtcStatsState,
  functionConnectionState,
  machineNameState,
  machineIdState,
  machineIdAttestationReplyState,
  nonsoleReadinessState,
  hostModeEntryResultState,
  truncMachineIdState,
  loginProfileState,
  hostRuleState,
  showStaticImageState,
  invitationSendState,
  preferenceState,
  webrtcPresetGroups,
  twitchSettingsState,
  processCatalogState,
  discordSettingsState,
  hostSettingsState,
  runningApplicationState,
  iceServerState,
  qrCodeBase64State,
  qrCodeGenerationState,
  hostGamepadState,
  gameClipConfigState,
  gameClipStatusState,
  hostInputDominanceState,
  automaticUpdatesEnabledState,
  playjectorHostModeState,
  regionOverrideState,
  windowState,
  vpnInfoState,
  afkPreventionState,
  playerNameState,
  adModeState,
  machineIdAttestationState,
  machineCredentialsState,
  touchInputConfigState,
  userProfileState
};

export const getSharedStores = () => {
  return sharedStateStores;
};
export const resetSharedStores = (destroyListeners?: boolean, exceptions?: string[]) => {
  Object.keys(sharedStateStores).forEach((storeName) => {
    if (!exceptions || !exceptions.includes(storeName)) {
      const store = sharedStateStores[storeName] as StateStore<any>;
      store.reset(destroyListeners);
    } else {
      console.log(`Skipping reset of ${storeName}`);
    }
  });
};
